import { useAccounts } from '../store/accounts'
import { useApiHealth } from '../store/apiHealth'
import { logFailure } from '../cache/db'

/** Pull the most useful identifying query param off a bsky xrpc path
 *  so the failure log can tell the operator WHICH actor / repo / uri
 *  failed instead of just "getAuthorFeed". Falls back to the bare
 *  path when nothing identifying is present. */
function targetLabel(path: string): string {
  const [base, qs] = path.split('?')
  if (!qs) return base
  const params = new URLSearchParams(qs)
  for (const key of ['actor', 'repo', 'uri', 'q', 'term']) {
    const v = params.get(key)
    if (v) {
      const short = v.length > 60 ? v.slice(0, 57) + '…' : v
      return `${base} [${key}=${short}]`
    }
  }
  return base
}

/** Map a raw error message to a friendlier sentence the failure log
 *  can show. Falls through to the original message when nothing
 *  matches. `retries` is the number of retries we already burned. */
function explainStatus(raw: string, retries: number): string {
  const suffix = retries > 0 ? ` (after ${retries} retry)` : ''
  if (/\b524\b/.test(raw)) {
    return `524 — upstream timeout, bsky AppView slow or worker stalled${suffix}`
  }
  if (/\b502\b/.test(raw)) return `502 — bad gateway${suffix}`
  if (/\b503\b/.test(raw)) return `503 — service unavailable${suffix}`
  if (/\b504\b/.test(raw)) return `504 — gateway timeout${suffix}`
  if (/\b429\b/.test(raw)) return `429 — rate limited${suffix}`
  if (/\b401\b/.test(raw)) return `401 — auth expired or invalid${suffix}`
  if (/\b404\b/.test(raw)) return `404 — not found${suffix}`
  return retries > 0 ? `${raw}${suffix}` : raw
}

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '')

function apiUrl(path: string): string {
  return `${API_BASE}${path}`
}

/** Bare worker call with an explicit service + bearer. Skips the
 *  account-bound auto-refresh path so callers using a short-lived
 *  service-auth token (e.g. for chat.bsky.* against api.bsky.chat)
 *  don't accidentally rotate the user's PDS accessJwt. */
export async function rawXrpc<T>(opts: {
  service: string
  bearerJwt: string
  path: string
  method?: 'GET' | 'POST'
  body?: unknown
  atprotoProxy?: string
}): Promise<T> {
  return jsonFetch<T>(apiUrl('/api/xrpc'), {
    method: 'POST',
    body: JSON.stringify({
      service: opts.service,
      accessJwt: opts.bearerJwt,
      path: opts.path,
      body: opts.body,
      method: opts.method ?? 'POST',
      atprotoProxy: opts.atprotoProxy,
    }),
  })
}

/** Client-side abort threshold — well under Cloudflare's 100s edge
 *  timeout so we don't sit waiting on a stalled upstream when bsky's
 *  AppView is slow. */
const FETCH_TIMEOUT_MS = 15_000

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  let res: Response
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    res = await fetch(url, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
      signal: controller.signal,
    })
  } catch (e) {
    // TypeError "Failed to fetch" — network is down, DNS failed, CORS
    // preflight rejected, or the worker host is unreachable. Surface
    // it to the global health banner so the UI can degrade
    // gracefully instead of every consumer popping its own toast.
    // AbortError is the client-side 15s timeout — treat as a slow
    // upstream rather than a network outage so callers can retry.
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new Error('524 — client timeout (15s, upstream did not respond)')
    }
    const msg = e instanceof Error ? e.message : String(e)
    useApiHealth.getState().reportNetworkDown(msg)
    throw new Error(`network: ${msg}`)
  } finally {
    clearTimeout(timeoutId)
  }
  if (res.status === 429) {
    // Distinguish Cloudflare daily quota (error 1027 in body) from
    // a transient bsky rate limit — they need different clear windows.
    const bodyText = await res.text().catch(() => '')
    let bodyJson: Record<string, unknown> = {}
    try { bodyJson = JSON.parse(bodyText) } catch { /* keep empty */ }
    const errors = Array.isArray(bodyJson.errors)
      ? (bodyJson.errors as { code?: number }[])
      : []
    const isCloudflareQuota =
      errors.some((e) => e.code === 1027) || /1027|daily quota/i.test(bodyText)

    if (isCloudflareQuota) {
      // Free-tier Cloudflare daily limit — block until UTC midnight.
      useApiHealth.getState().reportRateLimited()
      throw new Error('Cloudflare daily quota exhausted — resumes at UTC midnight.')
    }
    // Bluesky rate limit — transient. Use ratelimit-reset (Unix timestamp)
    // if present; fall back to 5 minutes.
    const resetHeader = res.headers.get('ratelimit-reset')
    const clearAfterMs = resetHeader
      ? Math.max(Number(resetHeader) * 1000 - Date.now(), 30_000)
      : 5 * 60 * 1000
    useApiHealth.getState().reportRateLimited(clearAfterMs)
    const bskyMsg = (bodyJson.message as string | undefined) ?? 'RateLimitExceeded'
    throw new Error(`429 ${bskyMsg}`)
  }
  if (!res.ok) {
    const text = await res.text()
    let msg = text
    try {
      msg = JSON.parse(text).error ?? JSON.parse(text).message ?? text
    } catch {
      /* keep raw */
    }
    throw new Error(msg || res.statusText)
  }
  // Successful round-trip — clear any prior block state.
  const h = useApiHealth.getState()
  if (h.rateLimited || h.networkDown) h.clear()
  return (await res.json()) as T
}

export interface SessionResp {
  did: string
  handle: string
  accessJwt: string
  refreshJwt: string
}

/** AppView reads that don't need auth — getAuthorFeed, getProfile,
 *  searchActors, searchPosts, getLikes, getRepostedBy, getQuotes,
 *  getPostThread, getFeed (public feeds), etc. Going direct from the
 *  browser avoids the Cloudflare-worker hop entirely, which is the
 *  layer that was timing out as 524s when bsky's AppView was slow.
 *
 *  CORS-open so this works straight from the browser. */
const APPVIEW_BASE = 'https://public.api.bsky.app'

async function appViewFetch<T>(path: string, opts: {
  accountDid?: string
}): Promise<T> {
  const url = `${APPVIEW_BASE}/xrpc/${path}`
  try {
    const r = await fetch(url, {
      signal: (() => {
        const c = new AbortController()
        setTimeout(() => c.abort(), FETCH_TIMEOUT_MS)
        return c.signal
      })(),
    })
    if (!r.ok) {
      const text = await r.text().catch(() => '')
      let msg = text
      try {
        msg = JSON.parse(text).error ?? JSON.parse(text).message ?? text
      } catch {
        /* keep raw */
      }
      throw new Error(`${r.status} ${msg || r.statusText}`)
    }
    return (await r.json()) as T
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new Error('524 — client timeout (15s, AppView did not respond)')
    }
    throw e
  }
}

export const api = {
  health: () => jsonFetch<{ ok: boolean }>(apiUrl('/api/health')),

  login: (input: {
    service: string
    identifier: string
    password: string
    authFactorToken?: string
  }) =>
    jsonFetch<SessionResp>(apiUrl('/api/auth/login'), {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  refresh: (input: { service: string; refreshJwt: string }) =>
    jsonFetch<SessionResp>(apiUrl('/api/auth/refresh'), {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * Authenticated XRPC call routed through the worker. The worker is
   * stateless — it just forwards with bearer auth so the browser doesn't
   * need to negotiate CORS with every PDS.
   */
  /**
   * Unauthenticated AppView read. Bypasses the Cloudflare worker
   * entirely and hits public.api.bsky.app from the browser. Use for
   * any read that doesn't need session auth (getProfile,
   * getAuthorFeed, searchActors, getLikes, …).
   *
   * Retries 524/502/503/504 once after 2s. Logs final failures.
   */
  async appView<T>(opts: { path: string; accountDid?: string }): Promise<T> {
    try {
      return await appViewFetch<T>(opts.path, { accountDid: opts.accountDid })
    } catch (firstErr) {
      const firstMsg = firstErr instanceof Error ? firstErr.message : String(firstErr)
      const transient = /\b(524|502|503|504)\b/.test(firstMsg)
      if (transient) {
        await new Promise((r) => setTimeout(r, 2000))
        try {
          return await appViewFetch<T>(opts.path, { accountDid: opts.accountDid })
        } catch (secondErr) {
          const reason = secondErr instanceof Error ? secondErr.message : String(secondErr)
          const m = reason.match(/(\d{3})/)
          void logFailure({
            op: 'appView',
            target: targetLabel(opts.path),
            accountDid: opts.accountDid,
            reason: explainStatus(reason, 1),
            httpStatus: m ? Number(m[1]) : undefined,
          })
          throw secondErr
        }
      }
      if (!/InvalidRequest/i.test(firstMsg)) {
        const m = firstMsg.match(/(\d{3})/)
        void logFailure({
          op: 'appView',
          target: targetLabel(opts.path),
          accountDid: opts.accountDid,
          reason: explainStatus(firstMsg, 0),
          httpStatus: m ? Number(m[1]) : undefined,
        })
      }
      throw firstErr
    }
  },

  async xrpc<T>(opts: {
    accountId: string
    path: string
    body?: unknown
    method?: 'GET' | 'POST'
    /** Optional `Atproto-Proxy` header value, e.g.
     *  `did:web:api.bsky.chat#bsky_chat` for `chat.bsky.*` lexicons.
     *  Forwarded by the worker to the user's PDS so it can route to
     *  the chat service. */
    atprotoProxy?: string
  }): Promise<T> {
    const acc = useAccounts.getState().accounts.find((a) => a.id === opts.accountId)
    if (!acc) throw new Error('account not found')
    if (acc.deactivated) {
      // Include the full DID — the handle can change (and a deactivated
      // account's handle is often stale), so the DID is the only stable
      // way to tell which account this "deactivated" error is about.
      throw new Error(
        `account deactivated [${acc.did}]: ${acc.deactivationReason ?? 'unknown'}`,
      )
    }
    const method = opts.method ?? 'POST'
    const retryable = method === 'GET'

    // Fast-path: if we already know the worker quota is exhausted and
    // this is a read, skip the doomed worker hop and go straight to
    // the public AppView. Writes (POST) still need the worker for auth.
    if (retryable && useApiHealth.getState().rateLimited) {
      return api.appView<T>({ path: opts.path, accountDid: acc.did })
    }

    // Build the request body fresh on each attempt so a refresh
    // between try 1 and try 2 actually picks up the new accessJwt.
    const initialAcc = acc
    function makePayload(): string {
      const cur =
        useAccounts.getState().accounts.find((a) => a.id === opts.accountId) ?? initialAcc
      return JSON.stringify({
        service: cur.service,
        accessJwt: cur.accessJwt,
        path: opts.path,
        body: opts.body,
        method,
        atprotoProxy: opts.atprotoProxy,
      })
    }
    async function once() {
      return jsonFetch<T>(apiUrl('/api/xrpc'), { method: 'POST', body: makePayload() })
    }

    // Inline auto-refresh: every xrpc call self-heals on
    // ExpiredToken without the caller having to wrap. A per-account
    // mutex prevents 20 parallel calls from each kicking off their
    // own refresh and rotating the refreshJwt out from under each
    // other.
    //
    // Returns:
    //   'refreshed' — caller should retry
    //   'not-expired' — caller logs the original error
    //   { refreshFailed: Error } — caller logs the REFRESH error, not the original
    type RefreshOutcome =
      | { kind: 'refreshed' }
      | { kind: 'not-expired' }
      | { kind: 'refreshFailed'; error: Error }
    async function refreshIfExpired(err: unknown): Promise<RefreshOutcome> {
      const msg = err instanceof Error ? err.message : String(err)
      if (DEACTIVATION_RE.test(msg)) {
        useAccounts.getState().markDeactivated(opts.accountId, extractCode(msg))
        return { kind: 'not-expired' }
      }
      if (!EXPIRED_TOKEN_RE.test(msg)) return { kind: 'not-expired' }
      const accNow = useAccounts.getState().accounts.find((a) => a.id === opts.accountId)
      if (!accNow)
        return {
          kind: 'refreshFailed',
          error: new Error('account row vanished mid-refresh'),
        }
      try {
        await refreshSessionOnce(opts.accountId, accNow)
        return { kind: 'refreshed' }
      } catch (refreshErr) {
        const rmsg = refreshErr instanceof Error ? refreshErr.message : String(refreshErr)
        if (DEACTIVATION_RE.test(rmsg)) {
          useAccounts.getState().markDeactivated(opts.accountId, extractCode(rmsg))
        } else if (EXPIRED_TOKEN_RE.test(rmsg)) {
          // The refresh token itself is expired. Mark the account
          // deactivated with our own 'SessionExpired' code so the
          // banner can tell the user to re-login, and so subsequent
          // xrpc calls short-circuit instead of piling more
          // "refresh failed" rows into the log.
          useAccounts.getState().markDeactivated(opts.accountId, 'SessionExpired')
        }
        return {
          kind: 'refreshFailed',
          error: refreshErr instanceof Error ? refreshErr : new Error(rmsg),
        }
      }
    }

    // First attempt — auto-refresh on ExpiredToken and retry once
    // (this is in addition to the transient-5xx retry further down).
    let firstErr: unknown
    let refreshFailureLogged = false
    try {
      return await once()
    } catch (err) {
      const outcome = await refreshIfExpired(err)
      if (outcome.kind === 'refreshed') {
        try {
          return await once()
        } catch (afterRefreshErr) {
          firstErr = afterRefreshErr
        }
      } else if (outcome.kind === 'refreshFailed') {
        // Log the REFRESH error with op='refresh' so the user sees
        // the real root cause ("refresh token expired — re-login
        // required") instead of a misleading ExpiredToken row.
        const rmsg = outcome.error.message
        void logFailure({
          op: 'refresh',
          target: targetLabel(opts.path),
          accountDid: acc.did,
          reason: `refresh failed: ${rmsg} — re-login required`,
        })
        refreshFailureLogged = true
        firstErr = err
      } else {
        firstErr = err
      }
    }

    const firstMsg = firstErr instanceof Error ? firstErr.message : String(firstErr)
    // Transient upstream errors: Cloudflare 524 (origin timeout),
    // 502 (bad gateway), 503 (unavailable), 504 (gateway timeout).
    // GETs only — POSTs skip the retry to avoid double-writes.
    const transient = /\b(524|502|503|504)\b/.test(firstMsg)
    if (retryable && transient) {
      await new Promise((r) => setTimeout(r, 2000))
      try {
        return await once()
      } catch (secondErr) {
        const reason = secondErr instanceof Error ? secondErr.message : String(secondErr)
        const m = reason.match(/(\d{3})/)
        void logFailure({
          op: 'xrpc',
          target: targetLabel(opts.path),
          accountDid: acc.did,
          reason: explainStatus(reason, 1),
          httpStatus: m ? Number(m[1]) : undefined,
        })
        throw secondErr
      }
    }
    // Any rate-limit (Cloudflare quota or bsky transient) — fall back
    // to the public AppView for reads. The AppView bypasses the worker
    // so authenticated quota doesn't apply; the state auto-clears when
    // the window elapses.
    const isRateLimit = /quota exhausted|daily quota|RateLimitExceeded|429/i.test(firstMsg)
    if (retryable && isRateLimit) {
      return api.appView<T>({ path: opts.path, accountDid: acc.did })
    }

    // InvalidRequest = expected API rejection (actor hid their likes/feed,
    // bad param, etc). Not an infrastructure failure — skip the log.
    if (!refreshFailureLogged && !/InvalidRequest/i.test(firstMsg)) {
      const m = firstMsg.match(/(\d{3})/)
      void logFailure({
        op: 'xrpc',
        target: targetLabel(opts.path),
        accountDid: acc.did,
        reason: explainStatus(firstMsg, 0),
        httpStatus: m ? Number(m[1]) : undefined,
      })
    }
    throw firstErr
  },

  /**
   * Upload a Blob to the active PDS via the binary pass-through endpoint.
   * Returns the PDS's blob ref, which the caller folds into a post
   * embed. The worker doesn't store anything — it just streams bytes.
   */
  async uploadBlob(opts: {
    accountId: string
    blob: Blob
  }): Promise<{ $type?: string; ref: { $link: string }; mimeType: string; size: number }> {
    const acc = useAccounts.getState().accounts.find((a) => a.id === opts.accountId)
    if (!acc) throw new Error('account not found')

    const initialAcc = acc
    async function attempt() {
      const fresh =
        useAccounts.getState().accounts.find((a) => a.id === opts.accountId) ?? initialAcc
      const r = await fetch(apiUrl('/api/blob'), {
        method: 'POST',
        headers: {
          'Content-Type': opts.blob.type || 'application/octet-stream',
          'x-bskyhq-service': fresh.service,
          'x-bskyhq-jwt': fresh.accessJwt,
        },
        body: opts.blob,
      })
      if (!r.ok) throw new Error(`uploadBlob ${r.status}: ${await r.text()}`)
      const data = (await r.json()) as {
        blob?: { ref: { $link: string }; mimeType: string; size: number }
      }
      if (!data.blob) throw new Error('uploadBlob returned no blob ref')
      return data.blob
    }

    try {
      return await attempt()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      if (!EXPIRED_TOKEN_RE.test(msg)) {
        void logFailure({
          op: 'uploadBlob',
          target: 'com.atproto.repo.uploadBlob',
          accountDid: acc.did,
          reason: msg,
        })
        throw err
      }
      // Token expired — share the refresh mutex with xrpc.
      const accNow = useAccounts.getState().accounts.find((a) => a.id === opts.accountId)
      if (!accNow) throw err
      try {
        await refreshSessionOnce(opts.accountId, accNow)
      } catch (refreshErr) {
        const rmsg = refreshErr instanceof Error ? refreshErr.message : String(refreshErr)
        if (DEACTIVATION_RE.test(rmsg)) {
          useAccounts.getState().markDeactivated(opts.accountId, extractCode(rmsg))
        } else if (EXPIRED_TOKEN_RE.test(rmsg)) {
          useAccounts.getState().markDeactivated(opts.accountId, 'SessionExpired')
        }
        void logFailure({
          op: 'refresh',
          target: 'com.atproto.repo.uploadBlob',
          accountDid: acc.did,
          reason: `refresh failed: ${rmsg} — re-login required`,
        })
        throw refreshErr
      }
      return await attempt()
    }
  },
}

/** PDS error codes that mean the account is dead — we should NOT
 *  retry, just mark it deactivated and let the cache do its job. */
const DEACTIVATION_RE =
  /AccountDeactivated|AccountTakedown|AccountSuspended|AccountInvalid|AccountNotFound|InvalidAccount|InvalidToken|deactivated|suspended|takedown/i

/** Token-expired markers across the various error shapes bsky emits.
 *  Broader than the original "ExpiredToken|expired|invalid_token|401"
 *  because the worker proxy passes XRPC errors through with prefixes
 *  ("400 ExpiredToken: jwt expired"), bsky.app sometimes returns just
 *  "Token has expired", and Cloudflare can wrap it as "401 …". */
const EXPIRED_TOKEN_RE =
  /ExpiredToken|jwt expired|token has expired|invalid_token|InvalidToken|\b401\b/i

/** Per-account refresh mutex — when N concurrent xrpc calls all see
 *  ExpiredToken at once, they should share ONE refresh call instead
 *  of each firing its own and racing the refreshJwt rotation (which
 *  invalidates the others as RefreshTokenAlreadyUsed). */
const inflightRefresh = new Map<string, Promise<SessionResp>>()

async function refreshSessionOnce(
  accountId: string,
  acc: { service: string; refreshJwt: string },
): Promise<SessionResp> {
  const existing = inflightRefresh.get(accountId)
  if (existing) return existing
  const p = (async () => {
    try {
      const fresh = await api.refresh({
        service: acc.service,
        refreshJwt: acc.refreshJwt,
      })
      useAccounts
        .getState()
        .updateTokens(accountId, fresh.accessJwt, fresh.refreshJwt)
      return fresh
    } finally {
      inflightRefresh.delete(accountId)
    }
  })()
  inflightRefresh.set(accountId, p)
  return p
}

/**
 * Wraps an authenticated call with auto-refresh: if the access token is
 * expired we refresh it once and retry. If the refresh itself reports
 * the account is deactivated / suspended / taken down, mark the
 * account as deactivated so live polls bail out and the UI can show
 * the cache-only banner.
 */
export async function withFreshAccess<T>(
  accountId: string,
  fn: () => Promise<T>,
): Promise<T> {
  // Short-circuit: don't even attempt an authenticated call on a
  // known-dead account. Callers degrade to cache-only.
  const accUp = useAccounts.getState().accounts.find((a) => a.id === accountId)
  if (accUp?.deactivated) {
    // Full DID, not handle — see note on the xrpc() guard above.
    throw new Error(
      `account deactivated [${accUp.did}]: ${accUp.deactivationReason ?? 'unknown'}`,
    )
  }
  try {
    return await fn()
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    // Hard-fail on deactivation — don't try to refresh.
    if (DEACTIVATION_RE.test(msg)) {
      useAccounts.getState().markDeactivated(accountId, extractCode(msg))
      throw err
    }
    if (!EXPIRED_TOKEN_RE.test(msg)) throw err
    const acc = useAccounts.getState().accounts.find((a) => a.id === accountId)
    if (!acc) throw err
    try {
      await refreshSessionOnce(accountId, acc)
    } catch (refreshErr) {
      const rmsg =
        refreshErr instanceof Error ? refreshErr.message : String(refreshErr)
      // The refresh endpoint is the canonical signal — if THIS rejects
      // with a deactivation code, the account is gone for good.
      if (DEACTIVATION_RE.test(rmsg)) {
        useAccounts.getState().markDeactivated(accountId, extractCode(rmsg))
      } else if (EXPIRED_TOKEN_RE.test(rmsg)) {
        useAccounts.getState().markDeactivated(accountId, 'SessionExpired')
      }
      throw refreshErr
    }
    return await fn()
  }
}

function extractCode(msg: string): string {
  const m = msg.match(
    /AccountDeactivated|AccountTakedown|AccountSuspended|AccountInvalid|AccountNotFound|InvalidAccount|InvalidToken/,
  )
  return m ? m[0] : msg.slice(0, 80)
}
